import { createSignal, onCleanup, onMount } from 'solid-js';
import { JSX } from 'solid-js/jsx-runtime';

import './video.scss';

export function Video(props: JSX.VideoHTMLAttributes<HTMLVideoElement>) {
	let videoRef: HTMLVideoElement | undefined;
	const [
		isPlaying,
		setIsPlaying
	] = createSignal(false);
	const [
		currentTime,
		setCurrentTime
	] = createSignal(0);
	const [
		duration,
		setDuration
	] = createSignal(0);
	const [
		volume,
		setVolume
	] = createSignal(1);
	const [
		isMuted,
		setIsMuted
	] = createSignal(false);

	const [
		buffered,
		setBuffered
	] = createSignal(0);

	const [
		showVolumeSlider,
		setShowVolumeSlider
	] = createSignal(false);

	const formatTime = (seconds: number) => {
		const mins = Math.floor(seconds / 60);
		const secs = Math.floor(seconds % 60);

		return `${mins}:${secs.toString()
			.padStart(2, '0')}`;
	};

	const togglePlay = (e: MouseEvent) => {
		if (!videoRef) {
			return;
		}

		e.stopPropagation();

		if (videoRef.paused) {
			videoRef.play();
		} else {
			videoRef.pause();
		}
	};

	const handleTimeUpdate = () => {
		if (!videoRef) {
			return;
		}

		setCurrentTime(videoRef.currentTime);
	};

	const handleLoadedMetadata = () => {
		if (!videoRef) {
			return;
		}

		setDuration(videoRef.duration);
	};

	const handleProgress = () => {
		if (!videoRef || !videoRef.buffered.length) {
			return;
		}

		const bufferedEnd = videoRef.buffered.end(videoRef.buffered.length - 1);
		const duration = videoRef.duration;
		setBuffered(
			duration > 0 ?
				bufferedEnd / duration * 100 :
				0
		);
	};

	const handleSeek = (e: MouseEvent) => {
		if (!videoRef) {
			return;
		}

		e.stopPropagation();

		const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
		const percent = (e.clientX - rect.left) / rect.width;

		videoRef.currentTime = percent * videoRef.duration;
	};

	const handleVolumeChange = (e: InputEvent) => {
		if (!videoRef) {
			return;
		}

		e.stopPropagation();

		const value = parseFloat((e.target as HTMLInputElement).value);

		videoRef.volume = value;
		setVolume(value);
		setIsMuted(value === 0);
	};

	const isMobileDevice = () => {
		return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
	};

	const toggleMute = () => {
		if (!videoRef) {
			return;
		}

		const newMutedState = !isMuted();
		videoRef.muted = newMutedState;
		setIsMuted(newMutedState);
	};

	const toggleVolumeSlider = (e: MouseEvent) => {
		e.stopPropagation();

		if (isMobileDevice()) {
			toggleMute();
		} else {
			setShowVolumeSlider(!showVolumeSlider());
		}
	};

	const toggleFullscreen = (e: MouseEvent) => {
		e.stopPropagation();

		if (!videoRef) {
			return;
		}

		if (!document.fullscreenElement) {
			videoRef.requestFullscreen();
		} else {
			document.exitFullscreen();
		}
	};

	onMount(() => {
		if (!videoRef) {
			return;
		}

		videoRef.addEventListener('play', () => setIsPlaying(true));
		videoRef.addEventListener('pause', () => setIsPlaying(false));
		videoRef.addEventListener('timeupdate', handleTimeUpdate);
		videoRef.addEventListener('loadedmetadata', handleLoadedMetadata);
		videoRef.addEventListener('progress', handleProgress);

		onCleanup(() => {
			if (!videoRef) {
				return;
			}

			videoRef.removeEventListener('play', () => setIsPlaying(true));
			videoRef.removeEventListener('pause', () => setIsPlaying(false));
			videoRef.removeEventListener('timeupdate', handleTimeUpdate);
			videoRef.removeEventListener('loadedmetadata', handleLoadedMetadata);
			videoRef.removeEventListener('progress', handleProgress);
		});
	});

	const aspectRatio = () => props.width && props.height ?
		`${props.width} / ${props.height}` :
		'16 / 9';

	return (
		<div class="video-container" style={`aspect-ratio: ${aspectRatio()}`}>
			<video
				ref={videoRef!}
				{...props}
				class="video" />

			<div class="video-controls">
				<button class="video-controls__play" onClick={togglePlay}>
					<svg viewBox="0 0 24 24">
						{isPlaying() ?
							<path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" /> :
							<path d="M8 5v14l11-7z" />}
					</svg>
				</button>

				<div class="video-controls__time">
					{formatTime(currentTime())} / {formatTime(duration())}
				</div>

				<div class="video-controls__timeline" onClick={handleSeek}>
					<div class="video-controls__timeline-track">
						<div
							class="video-controls__timeline-buffered"
							style={{ width: `${buffered()}%` }}></div>
						<div
							class="video-controls__timeline-progress"
							style={{ width: `${currentTime() / duration() * 100}%` }}></div>
					</div>
				</div>

				<div class="video-controls__volume-container">
					{showVolumeSlider() &&
						<div class="video-controls__volume-popup">
							<input
								type="range"
								class="video-controls__volume-slider"
								min="0"
								max="1"
								step="0.01"
								value={volume()}
								onClick={e => e.stopPropagation()}
								onInput={handleVolumeChange}
							/>
						</div>
					}

					<button class="video-controls__volume" onClick={toggleVolumeSlider}>
						<svg viewBox="0 0 24 24">
							{isMuted() || volume() === 0 ?
								<path
									d={'M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63z' +
										'm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28' +
										'-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5' +
										' 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69' +
										'-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z'} /> :
								<path
									d={'M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05' +
										'c1.48-.73 2.5-2.25 2.5-4.02z'} />}
						</svg>
					</button>
				</div>

				<button class="video-controls__fullscreen" onClick={toggleFullscreen}>
					<svg viewBox="0 0 24 24">
						<path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" />
					</svg>
				</button>
			</div>
		</div>
	);
}
